import React, { ChangeEvent, ReactElement, useState } from "react";
import {
  IColumn,
  IDictionaryNumericKey,
  IParentSize,
  IProduction,
  IStrandProduction,
  Order
} from "../../../types";
import {

  Table,
  TableBody,
  TableCell,
  TableHead, TablePagination,
  TableRow, TableSortLabel
} from "@mui/material";

import ProductionTableRow from "./productionTableRow";
import { IAuxiliaryData } from "../types";
import { toTons } from "../../../system/utils";
import TableContainer  from "../../../components/tableContainer";
import TablePaginationActions from "../../../components/tablePaginationActions";

interface IProps {
  productionData: IProduction[];
  parentSize: IParentSize;
  auxiliaryData: IDictionaryNumericKey<IAuxiliaryData> | null;
}

const columns: IColumn[] = [
  {
    id: "factoryDay",
    label: "Factory Day",
    minWidth: 0
  },
  {
    id: "shift",
    label: "Shift",
    minWidth: 0
  },
  {
    id: "crew",
    label: "Crew",
    minWidth: 0
  },
  {
    id: "millOrder",
    label: "Mill Order",
    minWidth: 0
  },
  {
    id: "millHeat",
    label: "Mill Heat",
    minWidth: 0
  },
  {
    id: "steel",
    label: "Steel",
    minWidth: 0
  },
  {
    id: "orderBillets",
    label: "Scheduled Billets",
    minWidth: 0,
    align: "right"
  },
  {
    id: "totalBillets",
    label: "Total Billets",
    minWidth: 0,
    align: "right"
  },
  {
    id: "accumulateBillets",
    label: "Accumulate Billets",
    minWidth: 0,
    align: "right"
  },
  {
    id: "accumulateCobbleBillets",
    label: "Accumulate Cobble",
    minWidth: 0,
    align: "right"
  },
  {
    id: "accumulateCutUpBillets",
    label: "Accumulate Cut up",
    minWidth: 0,
    align: "right"
  },
  {
    id: "totalWeight",
    label: "Total Weight [US ton]",
    minWidth: 0,
    align: "right",
    format: value => toTons(value).toFixed(2)

  },
  {
    id: "beginDatetime",
    label: "Begin",
    minWidth: 0
  },
  {
    id: "endDatetime",
    label: "End",
    minWidth: 0
  }
];



const stableSortExt = (array: IProduction[], auxiliary: IDictionaryNumericKey<IAuxiliaryData>, comparator: (a: IProduction, b: IProduction, auxiliary: IDictionaryNumericKey<IAuxiliaryData>) => number): IProduction[] => {
  const stabilizedThis = array.map((el, index) => [el, index] as [IProduction, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0], auxiliary);
    if (order !== 0) {
      return order;
    }

    return a[1] - b[1];
  });

  return stabilizedThis.map((el) => el[0]);
};



function descendingComparator(a: IProduction, b: IProduction, auxiliary: IDictionaryNumericKey<IAuxiliaryData>, orderBy: string) {
  const dataA = a.productionMachines[0] as IStrandProduction;
  const dataB = b.productionMachines[0] as IStrandProduction;
  const auxA = auxiliary[a.productionID];
  const auxB = auxiliary[b.productionID];

  if (orderBy === "factoryDay") {
    if (dataB.shiftHistory.factoryDay < dataA.shiftHistory.factoryDay) {
      return -1;
    }

    return dataB.shiftHistory.factoryDay > dataA.shiftHistory.factoryDay ? 1 : 0;
  }

  if (orderBy === "shift") {
    if (dataB.shiftHistory.name < dataA.shiftHistory.name) {
      return -1;
    }

    return dataB.shiftHistory.name > dataA.shiftHistory.name ? 1 : 0;
  }

  if (orderBy === "crew") {
    if (dataB.crew.name < dataA.crew.name) {
      return -1;
    }

    return dataB.crew.name > dataA.crew.name ? 1 : 0;
  }

  if (orderBy === "millOrder") {
    if (dataB.millHeat < dataA.millHeat) {
      return -1;
    }

    return dataB.millHeat > dataA.millHeat ? 1 : 0;
  }

  if (orderBy === "millHeat") {
    if (dataB.heatNumber < dataA.heatNumber) {
      return -1;
    }

    return dataB.heatNumber > dataA.heatNumber ? 1 : 0;
  }

  if (orderBy === "steel") {
    if (dataB.steelCode < dataA.steelCode) {
      return -1;
    }

    return dataB.steelCode > dataA.steelCode ? 1 : 0;
  }

  if (orderBy === "orderBillets") {
    if (dataB.totalBillets < dataA.totalBillets) {
      return -1;
    }

    return dataB.totalBillets > dataA.totalBillets ? 1 : 0;
  }

  if (orderBy === "accumulateBillets") {
    if (dataB.accumulateBillets < dataA.accumulateBillets) {
      return -1;
    }

    return dataB.accumulateBillets > dataA.accumulateBillets ? 1 : 0;
  }

  if (orderBy === "accumulateCutUpBillets") {
    if (dataB.accumulateCutUpBillets < dataA.accumulateCutUpBillets) {
      return -1;
    }

    return dataB.accumulateCutUpBillets > dataA.accumulateCutUpBillets ? 1 : 0;
  }

  if (orderBy === "accumulateCobbleBillets") {
    if (dataB.accumulateCobbleBillets < dataA.accumulateCobbleBillets) {
      return -1;
    }

    return dataB.accumulateCobbleBillets > dataA.accumulateCobbleBillets ? 1 : 0;
  }

  if (orderBy === "totalBillets") {
    if (auxB.billets < auxA.billets) {
      return -1;
    }

    return auxB.billets > auxA.billets ? 1 : 0;
  }

  if (orderBy === "totalWeight") {
    if (auxB.weight < auxA.weight) {
      return -1;
    }

    return auxB.weight > auxA.weight ? 1 : 0;
  }

  if (orderBy === "beginDatetime") {
    if (b.beginDatetime < a.beginDatetime) {
      return -1;
    }

    return b.beginDatetime > a.beginDatetime ? 1 : 0;
  }

  if (orderBy === "endDatetime" && b.endDatetime && a.endDatetime) {
    if (b.endDatetime < a.endDatetime) {
      return -1;
    }

    return b.endDatetime > a.endDatetime ? 1 : 0;
  }

  return 0;
}

function getComparator(
  auxiliary: IDictionaryNumericKey<IAuxiliaryData>,
  order: Order,
  orderBy: string
): (a: IProduction, b: IProduction, auxiliary: IDictionaryNumericKey<IAuxiliaryData>) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, auxiliary, orderBy)
    : (a, b) => -descendingComparator(a, b, auxiliary, orderBy);
}



const ProductionTable = ({ productionData, auxiliaryData, parentSize }: IProps): ReactElement => {
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<string>("beginDatetime");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const tableContainerHeight = parentSize.height - 50;
  const dataLength = productionData ? productionData.length : 0;
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - dataLength) : 0;

  const createSortHandler = (property: string) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number,) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <TableContainer style={{ height: tableContainerHeight, maxHeight: tableContainerHeight }}>
        <Table stickyHeader={true} size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell className="table-header"/>
              {columns.map((column) =>
                column.id !== ""
                  ? <TableCell
                    className="table-header"
                    key={column.id}
                    align={column.align}
                    sortDirection={orderBy === column.id ? order : false}>
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={createSortHandler(column.id as string)}>
                      {column.label}
                      {orderBy === column.id
                        ? <span className="cell-visuallyHidden">
                          {order === "desc" ? "sorted descending" : "sorted ascending"}
                        </span>
                        : null}
                    </TableSortLabel>
                  </TableCell>
                  : <TableCell className="table-header" style={{ width: 20 }}/>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            { auxiliaryData && stableSortExt(productionData, auxiliaryData, getComparator(auxiliaryData, order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) =>
                <ProductionTableRow key={`management-rm-row-${row.productionID}`} row={row} auxiliaryData={auxiliaryData} />
              )}
            {emptyRows > 0 &&
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6}/>
              </TableRow>
            }
          </TableBody>
        </Table>
      </TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, { label: "All", value: -1 }]}
              colSpan={columns.length + 1}
              count={dataLength}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableHead>
      </Table>
    </>
  );
};

export default ProductionTable;
